/**
 * Metadata registry for Local Trailer Hire advice guides.
 * Server-safe (CommonJS) so sitemap generation can require it.
 * Article body components live under containers/AdviceArticlePage/articles/.
 */

const ADVICE_BASE_PATH = '/advice';

const ADVICE_CATEGORIES = {
  costs: { id: 'costs', label: 'Costs & planning' },
  choosing: { id: 'choosing', label: 'Choosing a trailer' },
  towing: { id: 'towing', label: 'Towing & weights' },
  skills: { id: 'skills', label: 'Skills & safety' },
  checklists: { id: 'checklists', label: 'Checklists' },
};

/**
 * @typedef {Object} AdviceSource
 * @property {string} label
 * @property {string} url
 * @property {string} [note]
 */

/**
 * @typedef {Object} AdviceArticle
 * @property {string} slug
 * @property {string} title
 * @property {string} metaTitle
 * @property {string} metaDescription
 * @property {string} excerpt
 * @property {string} categoryId
 * @property {string} published
 * @property {string} updated
 * @property {string} reviewed
 * @property {string[]} tags
 * @property {string[]} relatedSlugs
 * @property {AdviceSource[]} sources
 * @property {string} [sourcesHeading]
 * @property {{ heading: string, linkName?: string, searchBar?: boolean, trailerTypeKeys?: string[], params?: object, description?: string, linkLabel?: string }} [cta]
 * @property {{ id: string, label: string }[]} toc
 */

/** @type {Record<string, AdviceArticle>} */
const adviceArticles = {
  'trailer-hire-cost-guide': {
    slug: 'trailer-hire-cost-guide',
    title: 'Trailer hire cost guide: rates, fees and what affects the price',
    metaTitle: 'Trailer Hire Cost Guide | Local Trailer Hire',
    metaDescription:
      'How much does trailer hire cost? Learn how 24-hour pricing, duration discounts, trailer type, location, and extras affect what you pay. Compare current marketplace prices before booking.',
    excerpt:
      'Typical rates, what drives the price, and how to compare listings fairly before you book.',
    categoryId: 'costs',
    published: '2026-07-29',
    updated: '2026-08-01',
    reviewed: '2026-08-01',
    tags: 'trailer hire cost, trailer rental prices, cage trailer hire cost',
    relatedSlugs: [
      'box-vs-cage-trailer',
      'moving-house-trailer-checklist',
      'how-much-can-my-car-tow',
    ],
    sources: [
      {
        label: 'Local Trailer Hire marketplace listings',
        url:
          '/s?address=Australia&bounds=-3.277086564834846%2C159.4202665952671%2C-48.192850418409755%2C109.55885861869491&origin=-25.7349684916223%2C134.489562606981',
        requestLocation: true,
        note:
          'Price snapshot generated from published Box, Cage, Enclosed, and Car trailer listings. Daily prices include the service fee and GST on that fee; optional extras are excluded.',
      },
    ],
    cta: {
      heading: 'Compare trailer hire near you',
      searchBar: true,
      description: 'Browse local trailers by type, price, and pick-up area.',
    },
    toc: [
      { id: 'pricing-24-hour', label: 'How 24-hour pricing works' },
      { id: 'worked-examples', label: 'Example hire calculations' },
      { id: 'included-in-price', label: 'What is included in the hire price?' },
      { id: 'other-costs', label: 'Other costs to consider' },
      { id: 'price-factors', label: 'What affects the hire price?' },
      { id: 'current-price-examples', label: 'Current trailer hire price examples' },
      { id: 'how-to-compare', label: 'How to compare trailer prices fairly' },
      { id: 'ways-to-save', label: 'Ways to keep trailer hire costs down' },
      { id: 'pricing-faqs', label: 'Frequently asked questions' },
    ],
  },
  'box-vs-cage-trailer': {
    slug: 'box-vs-cage-trailer',
    title: 'Box vs cage trailer: which should you hire?',
    metaTitle: 'Box vs Cage Trailer Hire | Local Trailer Hire',
    metaDescription:
      'Compare box and cage trailers for tip runs, furniture, green waste, and building materials. Choose the right hire trailer for your job.',
    excerpt:
      'A practical comparison of box and cage trailers so you can match the trailer type to the job.',
    categoryId: 'choosing',
    published: '2026-07-29',
    updated: '2026-07-29',
    reviewed: '2026-07-29',
    tags: 'box trailer, cage trailer, trailer types, trailer hire',
    relatedSlugs: [
      'trailer-hire-cost-guide',
      'trailer-weight-guide',
      'moving-house-trailer-checklist',
    ],
    sourcesHeading: 'Safety and towing resources',
    sources: [
      {
        label: 'National Transport Commission: Load Restraint Guide for Light Vehicles',
        url:
          'https://www.ntc.gov.au/sites/default/files/assets/files/Load-Restraint-Guide-for-light-vehicles-2018.pdf',
        note:
          'Practical guidance on selecting a suitable trailer and restraining different types of loads.',
      },
      {
        label: 'Your state or territory transport authority',
        url: 'https://www.ntc.gov.au/codes-and-guidelines/load-restraint-guide',
        note:
          'Check local towing, load-projection, and flagging requirements before carrying long or oversized items.',
      },
      {
        label: 'Local Trailer Hire: Safe hiring guide',
        url: 'https://www.localtrailerhire.com.au/help/safe-hiring-guide',
        note: 'Review practical checks for choosing, collecting, towing, and returning a trailer.',
      },
    ],
    cta: {
      heading: 'Browse box and cage trailers',
      searchBar: true,
      trailerTypeKeys: ['box', 'cage'],
      description: 'Find local box and cage trailers near your pick-up suburb.',
    },
    toc: [
      { id: 'quick-comparison', label: 'Quick comparison' },
      { id: 'cage-trailers', label: 'When a cage trailer wins' },
      { id: 'cage-payload', label: 'Cage sides do not increase payload' },
      { id: 'box-trailers', label: 'When a box trailer wins' },
      { id: 'other-types', label: 'When to look at other types' },
      { id: 'decision-checklist', label: 'Decision checklist' },
    ],
  },
  'how-much-can-my-car-tow': {
    slug: 'how-much-can-my-car-tow',
    title: 'How much can my car tow? Checklist to find your real limit',
    metaTitle: 'How Much Can My Car Tow? | Local Trailer Hire',
    metaDescription:
      'Learn how to calculate towing capacity using vehicle, towbar, trailer, ball-load and GCM limits, with a worked example and pick-up checklist.',
    excerpt:
      'A checklist and worked example using your handbook, towbar rating, and trailer plate to find the real towing limit.',
    categoryId: 'towing',
    published: '2026-07-29',
    updated: '2026-08-01',
    reviewed: '2026-08-01',
    tags: 'towing capacity, how much can my car tow, trailer towing Australia',
    relatedSlugs: ['trailer-weight-guide', 'box-vs-cage-trailer', 'reversing-a-trailer'],
    sources: [
      {
        label: 'RACQ: Trailer and Tow Vehicle Specifications',
        url: 'https://www.racq.com.au/car/towing/trailer-and-tow-vehicle-specifications',
      },
      {
        label: 'RACQ: Selecting a trailer / tow vehicle combination',
        url: 'https://www.racq.com.au/car/towing/selecting-a-trailer-tow-vehicle-combination',
      },
      {
        label: 'Department of Infrastructure: Vehicle Standards Bulletin 1 (Revision 6)',
        url:
          'https://www.infrastructure.gov.au/sites/default/files/documents/vehicle-standards-bulletin1-revision6-trailers-with-aggregate-trailer-mass-of-4-5tonnes-or-less-march2024.pdf',
        note:
          'Guidance primarily for the construction and certification of trailers up to 4.5 tonnes ATM; also defines ATM and GTM.',
      },
      {
        label: 'WA Department of Transport: Safe Towing Guide',
        url:
          'https://www.transport.wa.gov.au/getmedia/412a9df8-1773-47c4-b29f-51f70f697c7b/DVS_P_Safe_Towing_Guide.pdf',
        note: 'General safe-towing guidance; check your state or territory rules.',
      },
    ],
    cta: {
      heading: 'Find a suitable local trailer',
      searchBar: true,
      description:
        'Compare nearby trailers by type, size, tare, ATM, and brake setup. Confirm the listing against your vehicle handbook and towbar rating before booking.',
    },
    toc: [
      { id: 'lowest-applicable-rating', label: 'Your lowest applicable rating' },
      { id: 'find-vehicle-limits', label: 'Find your vehicle limits' },
      { id: 'worked-example', label: 'Worked example' },
      { id: 'match-trailer', label: 'Match the trailer to your car' },
      { id: 'brakes-and-controllers', label: 'Trailer brakes and controllers' },
      { id: 'state-rules', label: 'State and territory rules' },
      { id: 'pickup-day-check', label: 'Check again before leaving' },
      { id: 'towing-capacity-faq', label: 'Towing capacity FAQ' },
    ],
  },
  'trailer-weight-guide': {
    slug: 'trailer-weight-guide',
    title: 'Trailer weight guide: what tare, payload, ATM, GTM and ball load mean',
    metaTitle: 'Trailer Weight Guide: Tare, ATM, GTM | Local Trailer Hire',
    metaDescription:
      'Understand trailer tare, payload, ATM, GTM and ball load so you can hire and load safely. Includes guidance on checking the compliance plate / VIN plate.',
    excerpt:
      'Understand the numbers on a compliance plate / VIN plate and how tare, payload, ATM, GTM and ball load fit together.',
    categoryId: 'towing',
    published: '2026-07-29',
    updated: '2026-08-01',
    reviewed: '2026-08-01',
    tags: 'trailer weights, ATM, GTM, tare, payload, ball load',
    relatedSlugs: ['how-much-can-my-car-tow', 'box-vs-cage-trailer', 'trailer-hire-cost-guide'],
    sources: [
      {
        label: 'Department of Infrastructure: Vehicle Standards Bulletin 1 (Revision 6)',
        url:
          'https://www.infrastructure.gov.au/sites/default/files/documents/vehicle-standards-bulletin1-revision6-trailers-with-aggregate-trailer-mass-of-4-5tonnes-or-less-march2024.pdf',
        note: 'Official definitions of ATM and GTM for light trailers.',
      },
      {
        label: 'RACQ: Selecting a trailer / tow vehicle combination',
        url: 'https://www.racq.com.au/car/towing/selecting-a-trailer-tow-vehicle-combination',
      },
    ],
    cta: {
      heading: 'Check trailer weights before you hire',
      searchBar: true,
      description:
        'Compare nearby trailer listings by size and type. Before booking, check that the trailer’s ATM, expected loaded weight, brake setup and ball load are compatible with your vehicle and towbar.',
    },
    toc: [
      { id: 'why-weights-matter', label: 'Why trailer weights matter' },
      { id: 'trailer-plate', label: 'Where to find the numbers' },
      { id: 'definitions', label: 'Tare, payload, ATM, GTM, ball load' },
      { id: 'ratings-versus-weight', label: 'Maximum ratings are not actual weights' },
      { id: 'worked-example', label: 'Worked example' },
      { id: 'cargo-examples', label: 'Practical cargo examples' },
      { id: 'common-mistakes', label: 'Common trailer-weight mistakes' },
      { id: 'trailer-weight-faq', label: 'Trailer weight FAQ' },
    ],
  },
  'moving-house-trailer-checklist': {
    slug: 'moving-house-trailer-checklist',
    title: 'How to move house with a trailer: the complete checklist',
    metaTitle: 'How to Move House with a Trailer Checklist | Local Trailer Hire',
    metaDescription:
      'A practical end-to-end checklist for moving house with a hired trailer: choosing size, packing, loading, towing, unloading, and return. Suitable for apartment and house moves.',
    excerpt:
      'The complete checklist for choosing a trailer, packing, loading, towing, unloading, and returning safely.',
    categoryId: 'checklists',
    published: '2026-07-29',
    updated: '2026-08-01',
    reviewed: '2026-08-01',
    tags: 'moving house trailer, trailer move checklist, furniture trailer hire',
    relatedSlugs: ['box-vs-cage-trailer', 'trailer-hire-cost-guide', 'reversing-a-trailer'],
    sourcesHeading: 'Safety and moving resources',
    sources: [
      {
        label: 'National Transport Commission: Load Restraint Guide for Light Vehicles',
        url:
          'https://www.ntc.gov.au/sites/default/files/assets/files/Load-Restraint-Guide-for-light-vehicles-2018.pdf',
        note:
          'National guidance for light vehicles and trailers up to 4.5 tonnes, including restraint responsibilities and jurisdictional differences.',
      },
      {
        label:
          'Your state or territory road authority: towing, load projection and restraint rules',
        url: 'https://austroads.gov.au/about-austroads',
        note:
          'Use the Austroads member list to find your road authority, then check the rules that apply everywhere you will travel.',
      },
      {
        label: 'Local Trailer Hire: Safe hiring guide',
        url: 'https://www.localtrailerhire.com.au/help/safe-hiring-guide',
      },
      {
        label: 'Local Trailer Hire: Trailer weight guide',
        url: 'https://www.localtrailerhire.com.au/advice/trailer-weight-guide',
        note:
          'Explains tare, payload, ATM, GTM, and ball load when choosing and loading a trailer.',
      },
    ],
    cta: {
      heading: 'Hire a trailer for your move',
      searchBar: true,
      description: 'Find local cage, box or enclosed trailers near your suburb.',
    },
    toc: [
      { id: 'trailer-suitability', label: 'Decide whether a trailer suits the move' },
      { id: 'choose-trailer', label: 'Work out what size trailer you need' },
      { id: 'before-you-book', label: 'Before you book' },
      { id: 'one-week-before', label: 'One week before moving' },
      { id: 'packing-plan', label: 'Packing and staging' },
      { id: 'loading-and-securing', label: 'Loading and securing the trailer' },
      { id: 'final-checks', label: 'Final checks before leaving' },
      { id: 'on-the-road', label: 'On the road' },
      { id: 'unloading-safely', label: 'Unloading safely' },
      { id: 'return-checklist', label: 'Return checklist' },
      { id: 'moving-trailer-faq', label: 'Moving house with a trailer FAQ' },
    ],
  },
  'reversing-a-trailer': {
    slug: 'reversing-a-trailer',
    title: 'How to reverse a trailer: method, practice tips and common mistakes',
    metaTitle: 'How to Reverse a Trailer | Local Trailer Hire',
    metaDescription:
      'Learn a clear method for reversing a trailer, with practice tips, common mistakes, and when to get out and check your path.',
    excerpt:
      'A beginner-friendly reversing method, with practice tips and safety checks before you manoeuvre.',
    categoryId: 'skills',
    published: '2026-07-29',
    updated: '2026-07-29',
    reviewed: '2026-07-29',
    tags: 'reversing a trailer, trailer reversing tips, how to reverse a trailer',
    relatedSlugs: [
      'how-much-can-my-car-tow',
      'trailer-weight-guide',
      'moving-house-trailer-checklist',
    ],
    sources: [
      {
        label: 'Queensland Government: Towing vehicles and trailers',
        url: 'https://www.qld.gov.au/transport/vehicle-safety/towing/towing-vehicles-and-trailers',
        note: 'Recommends a reversing camera or a spotter and notes that reversing takes practice.',
      },
      {
        label: 'NSW Government: Towing a trailer or caravan',
        url:
          'https://www.nsw.gov.au/driving-boating-and-transport/roads-safety-and-rules/vehicle-safety-and-compliance/towing-a-caravan',
        note: 'Advises having someone outside the vehicle give directions while reversing.',
      },
      {
        label: 'Transport Accident Commission Victoria: Drivers and towing',
        url: 'https://www.tac.vic.gov.au/road-safety/road-users/drivers',
        note: 'Recommends practising reversing in a safe environment with someone guiding you.',
      },
    ],
    cta: {
      heading: 'Find a trailer that suits your vehicle and job',
      searchBar: true,
      description:
        'Compare local trailers, check their dimensions and weight ratings, and allow time at pick-up to become familiar with the trailer before entering traffic.',
    },
    toc: [
      { id: 'quick-answer', label: 'The basic rule' },
      { id: 'setup', label: 'Set up for success' },
      { id: 'method', label: 'Reversing method' },
      { id: 'straight-line', label: 'Straight-line reversing' },
      { id: 'small-trailers', label: 'Why small trailers are harder' },
      { id: 'practice', label: 'Practice drill' },
      { id: 'common-mistakes', label: 'Common mistakes' },
      { id: 'when-to-stop', label: 'When to stop and reset' },
      { id: 'faqs', label: 'Frequently asked questions' },
    ],
  },
};

const adviceArticleSlugs = Object.keys(adviceArticles);

const getAdviceArticle = slug => adviceArticles[slug] || null;

const createAdviceArticlePath = slug => `${ADVICE_BASE_PATH}/${slug}`;

const getAdviceArticlesByCategory = () => {
  const grouped = Object.values(ADVICE_CATEGORIES).map(category => ({
    ...category,
    articles: adviceArticleSlugs
      .map(slug => adviceArticles[slug])
      .filter(article => article.categoryId === category.id),
  }));
  return grouped.filter(group => group.articles.length > 0);
};

const getRelatedAdviceArticles = slug => {
  const article = getAdviceArticle(slug);
  if (!article) {
    return [];
  }
  return article.relatedSlugs.map(getAdviceArticle).filter(Boolean);
};

/** Exactly three advice guides for hire location pages (national / state / suburb). */
const LOCATION_PAGE_ADVICE_GUIDE_SLUGS = [
  'trailer-hire-cost-guide',
  'box-vs-cage-trailer',
  'how-much-can-my-car-tow',
];

/** @deprecated Use LOCATION_PAGE_ADVICE_GUIDE_SLUGS — kept as an alias for suburb callers. */
const SUBURB_LANDING_ADVICE_SLUGS = LOCATION_PAGE_ADVICE_GUIDE_SLUGS;

const getLocationPageAdviceArticles = () =>
  LOCATION_PAGE_ADVICE_GUIDE_SLUGS.map(getAdviceArticle).filter(Boolean);

const getSuburbLandingAdviceArticles = () => getLocationPageAdviceArticles();

module.exports = {
  ADVICE_BASE_PATH,
  ADVICE_CATEGORIES,
  LOCATION_PAGE_ADVICE_GUIDE_SLUGS,
  SUBURB_LANDING_ADVICE_SLUGS,
  adviceArticles,
  adviceArticleSlugs,
  getAdviceArticle,
  createAdviceArticlePath,
  getAdviceArticlesByCategory,
  getRelatedAdviceArticles,
  getLocationPageAdviceArticles,
  getSuburbLandingAdviceArticles,
};
